import { useCallback, useEffect, useState } from 'react'
import { apiGet, adminMutate, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import './rh.css'

/**
 * SIRH LOTS 4-5 — PORTAIL SALARIÉ « Mon espace » (/moi).
 *
 * Mobile-first : le salarié consulte SON planning, SES heures (prévu /
 * pointé / validé — transparence totale, y compris les corrections
 * motivées), SES compteurs (solde + mouvements, jamais un solde opaque),
 * SES demandes d'absence et SES disponibilités. Il définit aussi son code
 * personnel de pointage. Rien d'autrui n'est accessible ici.
 */


/* Adaptateurs locaux : toast (kind,texte), adminMutate ({status,data}) et
   Modal (ariaLabel) du design system, avec l'ergonomie compacte des pages Temps. */
function useNotify(): (msg: string, kind?: 'success' | 'error' | 'info') => void {
  const raw = useToast()
  // STABLE entre les renders : ce hook entre dans les deps des useCallback de
  // chargement — une identité neuve à chaque render créerait une boucle infinie
  // de requêtes (vu en recette Chrome, corrigé ici).
  return useCallback((msg: string, kind: 'success' | 'error' | 'info' = 'success') => raw(kind === 'error' ? 'error' : 'ok', msg), [raw])
}
async function mutate<T extends { error?: string; message?: string }>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await adminMutate<T>(method, path, body)
  if (r.status >= 400 && !(r.data as { error?: string }).error) {
    return { ...(r.data as T), error: 'ERROR' }
  }
  return r.data
}

const fmtClock = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
const frDay = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
const frShort = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })
const fmtMin = (m: number) => `${m < 0 ? '−' : ''}${Math.floor(Math.abs(m) / 60)} h ${String(Math.abs(Math.round(m)) % 60).padStart(2, '0')}`

const TABS = [
  ['planning', 'Mon planning'], ['heures', 'Mes heures'], ['compteurs', 'Mes compteurs'],
  ['demandes', 'Mes demandes'], ['disponibilites', 'Mes disponibilités'],
] as const

export function MoiPage({ section }: { section?: string }) {
  const [tab, setTab] = useState<string>(section && TABS.some(([k]) => k === section) ? section : 'planning')
  return (
    <main className="lc-page lc-page--readable lc-stagger moi">
      <header className="hub-hero">
        <p className="hub-hero__kicker">Mon espace</p>
        <h1 className="lc-title">Mon temps de travail</h1>
      </header>
      <nav className="lc-tabs rh-tabs moi-tabs" aria-label="Sections">
        {TABS.map(([k, label]) => (
          <button key={k} className={`lc-tabs__tab${tab === k ? ' lc-tabs__tab--active' : ''}`} onClick={() => setTab(k)}>{label}</button>
        ))}
      </nav>
      {tab === 'planning' ? <MoiPlanning /> : tab === 'heures' ? <MoiHeures />
        : tab === 'compteurs' ? <MoiCompteurs /> : tab === 'demandes' ? <MoiDemandes /> : <MoiDispos />}
    </main>
  )
}

function MoiPlanning() {
  const [data, setData] = useState<{ week: string; shifts: Array<{ id: string; starts_at: string; ends_at: string; location_name: string | null; function_name: string | null }> } | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    // apiGet RÉSOUT aussi les 404 ({error}) — piège connu : on vérifie la forme.
    apiGet<Record<string, unknown>>('/api/v1/moi/planning')
      .then((r) => {
        if (r && Array.isArray(r['shifts'])) setData(r as unknown as NonNullable<typeof data>)
        else setError(typeof r?.['message'] === 'string' ? r['message'] as string : 'Aucun dossier salarié lié à ce compte.')
      })
      .catch(() => setError('Chargement impossible.'))
  }, [])
  if (error) return <p className="lc-hint">{error}</p>
  if (!data) return <p className="lc-hint">Chargement…</p>
  return (
    <section className="lc-card tv-block">
      <h2 className="tv-block__title">Semaine du {frShort(data.week)}</h2>
      {data.shifts.length === 0 ? <p className="lc-hint">Aucun shift prévu cette semaine.</p> : (
        <div className="tv-movs">
          {data.shifts.map((s) => (
            <div key={s.id} className="tv-mov">
              <span className="tv-mov__amt">{fmtClock(s.starts_at)}</span>
              <span className="tv-mov__body">
                {fmtClock(s.starts_at)} → {fmtClock(s.ends_at)}{s.location_name ? ` · ${s.location_name}` : ''}{s.function_name ? ` · ${s.function_name}` : ''}
                <em>{new Date(s.starts_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })}</em>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function MoiHeures() {
  const [rows, setRows] = useState<Array<{
    work_date: string
    planned: { starts_at: string; ends_at: string } | null
    clocked: { starts_at: string; ends_at: string } | null
    approved: { starts_at: string; ends_at: string; minutes: number; status: string; correction_reason_code: string | null } | null
  }> | null>(null)
  useEffect(() => { apiGet<{ history?: NonNullable<typeof rows> }>('/api/v1/moi/temps').then((r) => setRows(r.history ?? [])).catch(() => setRows([])) }, [])
  if (!rows) return <p className="lc-hint">Chargement…</p>
  if (rows.length === 0) return <p className="lc-hint">Aucune journée sur les 30 derniers jours.</p>
  return (
    <div className="moi-days">
      {rows.map((r) => (
        <article key={r.work_date} className="lc-card tv-block moi-day">
          <h2 className="tv-block__title">{frDay(r.work_date)}</h2>
          <div className="tv-day__cols">
            <div className="tv-col"><span className="tv-col__k">Prévu</span>
              <span className="tv-col__v">{r.planned ? `${fmtClock(r.planned.starts_at)} → ${fmtClock(r.planned.ends_at)}` : '—'}</span></div>
            <div className="tv-col"><span className="tv-col__k">Pointé</span>
              <span className="tv-col__v">{r.clocked ? `${fmtClock(r.clocked.starts_at)} → ${fmtClock(r.clocked.ends_at)}` : '—'}</span></div>
            <div className="tv-col"><span className="tv-col__k">Validé</span>
              <span className="tv-col__v">
                {r.approved && r.approved.status !== 'PROPOSED' ? `${fmtClock(r.approved.starts_at)} → ${fmtClock(r.approved.ends_at)} (${fmtMin(r.approved.minutes)})` : 'En attente'}
              </span></div>
          </div>
          {r.approved?.correction_reason_code ? (
            <p className="lc-hint">Heures ajustées par la Direction — motif : {({
              OUBLI_POINTAGE: 'oubli de pointage', ERREUR_POINTAGE: 'erreur de pointage',
              TEMPS_NON_TRAVAILLE: 'temps non travaillé', DEPASSEMENT_VALIDE: 'dépassement validé', AUTRE: 'autre',
            } as Record<string, string>)[r.approved.correction_reason_code] ?? r.approved.correction_reason_code}.</p>
          ) : null}
        </article>
      ))}
    </div>
  )
}

function MoiCompteurs() {
  const [data, setData] = useState<{
    accounts: Array<{ account_id: string; type_label: string; unit: string; balance: number }>
    movements: Record<string, Array<{ id: string; amount: number; reason: string; created_at: string }>>
  } | null>(null)
  useEffect(() => {
    apiGet<NonNullable<typeof data>>('/api/v1/moi/compteurs')
      .then((r) => setData(r && Array.isArray(r.accounts) ? r : { accounts: [], movements: {} }))
      .catch(() => setData({ accounts: [], movements: {} }))
  }, [])
  if (!data) return <p className="lc-hint">Chargement…</p>
  if (data.accounts.length === 0) return <p className="lc-hint">Aucun compteur ouvert pour l’instant.</p>
  return (
    <div className="moi-days">
      {data.accounts.map((a) => (
        <section key={a.account_id} className="lc-card tv-block">
          <h2 className="tv-block__title">{a.type_label} — <strong>{a.unit === 'MINUTES' ? fmtMin(a.balance) : `${a.balance} j`}</strong></h2>
          {(data.movements[a.account_id] ?? []).length === 0 ? <p className="lc-hint">Aucun mouvement.</p> : (
            <div className="tv-movs">
              {(data.movements[a.account_id] ?? []).map((m) => (
                <div key={m.id} className="tv-mov">
                  <span className={`tv-mov__amt${m.amount >= 0 ? ' tv-delta--plus' : ' tv-delta--minus'}`}>{m.amount >= 0 ? '+' : ''}{m.amount}</span>
                  <span className="tv-mov__body">{m.reason}<em>{new Date(m.created_at).toLocaleDateString('fr-FR')}</em></span>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
      <MoiPin />
    </div>
  )
}

function MoiPin() {
  const toast = useNotify()
  const [pin, setPin] = useState('')
  return (
    <section className="lc-card tv-block">
      <h2 className="tv-block__title">Mon code de pointage</h2>
      <p className="lc-hint">4 à 6 chiffres, pour pointer sur les stations Gestion Pointeuse. Jamais stocké en clair.</p>
      <div className="tv-credrow">
        <input className="lc-input tv-pininput" value={pin} inputMode="numeric" maxLength={6}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} placeholder="Nouveau code" aria-label="Nouveau code" />
        <button className="lc-btn lc-btn--primary" disabled={!/^\d{4,6}$/.test(pin)} onClick={() => {
          void mutate<{ error?: string; message?: string }>('POST', '/api/v1/moi/pin', { pin }).then((r) => {
            if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
            toast('Code personnel enregistré.', 'success'); setPin('')
          })
        }}>Enregistrer</button>
      </div>
    </section>
  )
}

function MoiDemandes() {
  const toast = useNotify()
  const [data, setData] = useState<{
    requests: Array<{ id: string; type_label: string; starts_on: string; ends_on: string; status: string; decision_note: string | null }>
    types: Array<{ id: string; label: string }>
  } | null>(null)
  const [form, setForm] = useState({ type_id: '', starts_on: '', ends_on: '', comment: '' })
  const load = useCallback(() => {
    apiGet<NonNullable<typeof data>>('/api/v1/moi/demandes')
      .then((r) => setData(r && Array.isArray(r.requests) ? r : { requests: [], types: [] }))
      .catch(() => setData({ requests: [], types: [] }))
  }, [])
  useEffect(() => { load() }, [load])
  if (!data) return <p className="lc-hint">Chargement…</p>
  const statusLabel: Record<string, string> = { REQUESTED: 'En attente', APPROVED: 'Approuvée', REJECTED: 'Refusée', CANCELLED: 'Annulée' }
  return (
    <div className="moi-days">
      <section className="lc-card tv-block">
        <h2 className="tv-block__title">Nouvelle demande</h2>
        <Field label="Type d'absence">
          <select className="lc-input" value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })}>
            <option value="">Choisir…</option>
            {data.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <div className="tv-credrow">
          <Field label="Du"><input type="date" className="lc-input" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} /></Field>
          <Field label="Au"><input type="date" className="lc-input" value={form.ends_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} /></Field>
        </div>
        <Field label="Commentaire (facultatif)"><TextInput value={form.comment} onChange={(v) => setForm({ ...form, comment: v })} /></Field>
        <button className="lc-btn lc-btn--primary" disabled={!form.type_id || !form.starts_on || !form.ends_on} onClick={() => {
          void mutate<{ error?: string; message?: string }>('POST', '/api/v1/moi/demandes', {
            type_id: form.type_id, starts_on: form.starts_on, ends_on: form.ends_on,
            ...(form.comment.trim() ? { comment: form.comment.trim() } : {}),
          }).then((r) => {
            if (r.error) { toast(r.message ?? 'Demande impossible.', 'error'); return }
            toast('Demande envoyée à la Direction.', 'success')
            setForm({ type_id: '', starts_on: '', ends_on: '', comment: '' }); load()
          })
        }}>Envoyer la demande</button>
      </section>
      <section className="lc-card tv-block">
        <h2 className="tv-block__title">Mes demandes</h2>
        {data.requests.length === 0 ? <p className="lc-hint">Aucune demande.</p> : (
          <div className="tv-movs">
            {data.requests.map((r) => (
              <div key={r.id} className="tv-mov">
                <span className={`tv-availchip tv-availchip--${r.status === 'APPROVED' ? 'AVAILABLE' : r.status === 'REJECTED' ? 'UNAVAILABLE' : 'none'}`}>
                  {r.status === 'APPROVED' ? '✓' : r.status === 'REJECTED' ? '✕' : '…'}
                </span>
                <span className="tv-mov__body">
                  {r.type_label} · du {frShort(r.starts_on)} au {frShort(r.ends_on)}
                  <em>{statusLabel[r.status] ?? r.status}{r.decision_note ? ` — ${r.decision_note}` : ''}</em>
                </span>
                {r.status === 'REQUESTED' ? (
                  <button className="lc-btn" onClick={() => {
                    void mutate('POST', `/api/v1/moi/demandes/${r.id}/annuler`, {}).then(() => load())
                  }}>Annuler</button>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function MoiDispos() {
  const toast = useNotify()
  const [rows, setRows] = useState<Array<{ id: string; title: string; target_date: string; message: string | null; respond_until: string; status: string; my_status: string | null; my_from: number | null; my_to: number | null }> | null>(null)
  const [partial, setPartial] = useState<{ id: string; from: string; to: string } | null>(null)
  const load = useCallback(() => { apiGet<{ campaigns?: NonNullable<typeof rows> }>('/api/v1/moi/disponibilites').then((r) => setRows(r.campaigns ?? [])).catch(() => setRows([])) }, [])
  useEffect(() => { load() }, [load])
  if (!rows) return <p className="lc-hint">Chargement…</p>
  if (rows.length === 0) return <p className="lc-hint">Aucune demande de disponibilités en cours.</p>

  async function respond(id: string, status: 'AVAILABLE' | 'UNAVAILABLE' | 'PARTIAL', from?: number, to?: number) {
    const r = await mutate<{ error?: string; message?: string }>('POST', `/api/v1/moi/disponibilites/${id}`, {
      status, ...(status === 'PARTIAL' ? { from_minutes: from, to_minutes: to } : {}),
    })
    if (r.error) { toast(r.message ?? 'Échec.', 'error'); return }
    toast('Réponse enregistrée. Merci !', 'success'); setPartial(null); load()
  }
  const toMinutes = (hhmm: string, nextDay: boolean) => {
    const [h, m] = hhmm.split(':').map(Number)
    return (h ?? 0) * 60 + (m ?? 0) + (nextDay ? 1440 : 0)
  }

  return (
    <div className="moi-days">
      {rows.map((c) => (
        <section key={c.id} className="lc-card tv-block">
          <h2 className="tv-block__title">{c.title}</h2>
          <p className="lc-hint">{frDay(c.target_date)}{c.message ? ` — ${c.message}` : ''} · réponse avant le {frShort(c.respond_until.slice(0, 10))}</p>
          {c.my_status ? (
            <p className="moi-answered">
              Votre réponse : <strong>{c.my_status === 'AVAILABLE' ? 'Disponible' : c.my_status === 'UNAVAILABLE' ? 'Indisponible' : 'Partiellement disponible'}</strong>
              {c.my_status === 'PARTIAL' && c.my_from != null && c.my_to != null
                ? ` (${String(Math.floor((c.my_from % 1440) / 60)).padStart(2, '0')}:${String(c.my_from % 60).padStart(2, '0')} → ${String(Math.floor((c.my_to % 1440) / 60)).padStart(2, '0')}:${String(c.my_to % 60).padStart(2, '0')})` : ''}
              {c.status === 'OPEN' ? ' — modifiable ci-dessous.' : ''}
            </p>
          ) : null}
          {c.status === 'OPEN' ? (
            <div className="moi-availbtns">
              <button className="lc-btn lc-btn--primary" onClick={() => void respond(c.id, 'AVAILABLE')}>Disponible</button>
              <button className="lc-btn" onClick={() => void respond(c.id, 'UNAVAILABLE')}>Indisponible</button>
              <button className="lc-btn" onClick={() => setPartial({ id: c.id, from: '22:00', to: '05:00' })}>Partiellement</button>
            </div>
          ) : <p className="lc-hint">Demande close.</p>}
          {partial?.id === c.id ? (
            <div className="tv-credrow">
              <Field label="Disponible de"><input type="time" className="lc-input" value={partial.from} onChange={(e) => setPartial({ ...partial, from: e.target.value })} /></Field>
              <Field label="à"><input type="time" className="lc-input" value={partial.to} onChange={(e) => setPartial({ ...partial, to: e.target.value })} /></Field>
              <button className="lc-btn lc-btn--primary" onClick={() => {
                const from = toMinutes(partial.from, false)
                let to = toMinutes(partial.to, false)
                if (to <= from) to += 1440 // plage de nuit : 22:00 → 05:00
                void respond(c.id, 'PARTIAL', from, to)
              }}>Valider</button>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  )
}
